import React from 'react'
import PropTypes from 'prop-types'
import { Box, Chip, Stack, Typography } from '@mui/material'
import { useSmartTranslation } from '../../../generic/hooks/useSmartTranslation'
import { useDateFormatter } from '../../../generic/hooks/useDateFormatter'

/**
 * Una máquina en la que un job sigue abierto: su nombre, el modo en que corre allí, quién abrió ese
 * período y desde cuándo — las tres cosas que un operario sopesa antes de cerrar el trabajo de otro.
 *
 * Nació dentro del diálogo del cierre INDIVIDUAL (#2935) y la **ENMIENDA 12 (G3-17)** la extrae aquí
 * **sin cambiarle el comportamiento**, porque el PO pidió que el cierre EN LOTE enseñe exactamente lo
 * mismo. 🔒 Se extrae en vez de copiarse: **dos filas que dicen lo mismo con dos códigos distintos
 * divergen en la primera corrección** — la misma lección de R-16 que ya nos costó una superficie sin
 * aviso de cierre.
 *
 * ⚠️ **AUSENCIA SEGURA (RN69/POL7):** los extras que Sun no resuelva **simplemente no se pintan** — ni
 * guion, ni hueco, ni marcador que pueda leerse como un valor. **Nunca la palabra "null"**, que es
 * literalmente el defecto que el PO vio en el diálogo de cierre en bloque (S240).
 */
const ActiveDeviceRow = ({ device }) => {
  const { t_ } = useSmartTranslation()
  const { formatDateTime } = useDateFormatter()

  const workerName = [device.worker?.name, device.worker?.surname].filter(Boolean).join(' ')
  const detail = [workerName, formatDateTime(device.since)].filter(Boolean).join(' · ')

  return (
    <Box sx={{ p: 1.5, borderRadius: 1, border: 1, borderColor: 'divider', bgcolor: 'action.hover' }}>
      <Stack direction='row' alignItems='center' spacing={1} flexWrap='wrap'>
        <Typography variant='subtitle2' fontWeight='bold'>{device.name}</Typography>
        {device.statusCode && (
          <Chip label={t_(device.statusCode)} color='warning' size='small' variant='outlined' />
        )}
      </Stack>
      {detail && (
        <Typography variant='caption' color='text.secondary'>{detail}</Typography>
      )}
    </Box>
  )
}

ActiveDeviceRow.propTypes = {
  device: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    name: PropTypes.string,
    statusCode: PropTypes.string,
    since: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    worker: PropTypes.shape({ name: PropTypes.string, surname: PropTypes.string })
  }).isRequired
}

export default ActiveDeviceRow
